/** Trading Identity / Trading Style Profile slice — the reference `tiRuleList()`, `tiSetRules(fn)` and `tiModel()`
 *  (design-reference script lines 3571–3726), ported verbatim. Data (TI_STYLES, TI_EXP, TI_SESSIONS, TI_INSTRUMENTS,
 *  TI_SYMGROUP, TI_SYSTEM_RULES) is read through `c.data.style`. */
import type { ChangeEvent } from 'react';
import type { TiStyle } from '@pointfive/contracts';
import type { Ctx } from './ctx';
import type { TiRule } from './state';
import type { ViewModel } from '@/screens/generated/view-model';

type FieldEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

/** Reference present (2026-07-24 08:40 UTC) rendered the way the reference renders `tiSave`'s timestamp (HH:MM). */
const TI_SAVED_AT = '08:40';

function systemRules(c: Ctx): TiRule[] {
  return c.data.style.systemRules.map((text, i) => ({ id: 'sys' + i, text, on: true, sys: true }));
}
/** Reference `tiRuleList()` — the user's rule list, or the platform defaults when none has been edited yet. */
export function tiRuleList(c: Ctx): TiRule[] {
  if (c.S.tiRules) return c.S.tiRules;
  return systemRules(c);
}
/** Reference `tiSetRules(fn)` — apply `fn` to a copy of the current list and mark the profile unsaved. */
export function tiSetRules(c: Ctx, fn: (rules: TiRule[]) => TiRule[]): void {
  const next = fn(tiRuleList(c).slice());
  c.set({ tiRules: next, tiSaved: null });
}
/** Deterministic stand-in for the reference's `'own' + Date.now()` id: next free numeric suffix among the user's own rules. */
function nextOwnId(rules: TiRule[]): string {
  let max = 0;
  rules.forEach((r) => {
    const m = /^own(\d+)$/.exec(r.id);
    if (m && m[1] !== undefined) max = Math.max(max, parseInt(m[1], 10));
  });
  return 'own' + (max + 1);
}

export function buildStyle(c: Ctx) {
  const S = c.S;
  const D = c.data.style;
  const rows = c.trades();
  const pick = (k: string): TiStyle => {
    const hit = D.styles.filter((s) => s[0] === k)[0] || D.styles[1];
    if (!hit) throw new Error('style dataset is empty');
    return hit;
  };
  const cur = pick(S.tiStyle);
  const chip = (on: boolean) => ({
    on,
    fw: on ? '600' : '500',
    bd: on ? 'color-mix(in srgb, var(--acc-fill) 45%, transparent)' : 'var(--l10)',
    bg: on ? 'color-mix(in srgb, var(--acc-fill) 12%, transparent)' : 'transparent',
    fg: on ? 'var(--acc)' : 'var(--muted)',
  });
  const dur = rows
    .map((r) => r[9])
    .filter((v) => typeof v === 'number' && v > 0)
    .sort((a, b) => a - b);
  const median = dur.length ? (dur[Math.floor(dur.length / 2)] ?? 0) : 0;
  const days: Record<number, number> = {};
  rows.forEach((r) => {
    days[r[1]] = 1;
  });
  const activeDays = Math.max(1, Object.keys(days).length);
  const perDay = rows.length / activeDays;
  const holdStr =
    median >= 3600
      ? (median / 3600).toFixed(1) + 'h'
      : median >= 60
        ? Math.round(median / 60) + 'm'
        : median + 's';
  const detectKey =
    median < 900
      ? 'scalper'
      : median < 14400
        ? 'day_trader'
        : median < 604800
          ? 'swing_trader'
          : 'position_trader';
  const det = S.tiDetected ? pick(S.tiDetected) : null;
  const maxN = parseInt(S.tiMax, 10);
  const riskN = parseFloat(S.tiRisk);
  const worst = maxN > 0 && riskN > 0 ? maxN * riskN : null;
  const rrParts = String(S.tiRR)
    .split(':')
    .map((v) => parseFloat(v));
  const rr0 = rrParts[0],
    rr1 = rrParts[1];
  const rrOk = rrParts.length === 2 && rr0 !== undefined && rr1 !== undefined && rr0 > 0 && rr1 > 0;
  const breakeven = rrOk && rr0 !== undefined && rr1 !== undefined ? (rr0 / (rr0 + rr1)) * 100 : null;
  const traded: Record<string, number> = {};
  rows.forEach((r) => {
    const g = D.symGroup[r[3]];
    if (g) traded[g] = (traded[g] || 0) + 1;
  });
  const uncovered = Object.keys(traded).filter((g) => S.tiInst.indexOf(g) < 0);
  const goalWords = S.tiGoals.trim() ? S.tiGoals.trim().split(/\s+/).length : 0;
  const sessList = S.tiSess.length ? S.tiSess.join(', ') : 'no sessions';
  return {
    tiLabel: cur[1],
    tiIcon: cur[3],
    tiColour: cur[6],
    tiDescription: cur[7],
    tiIsCustom: S.tiStyle === 'custom',
    tiExpLabel: (D.exp.filter((e) => e[0] === S.tiExp)[0] || ['', 'Not set'])[1],
    tiHasDetected: !!det,
    tiDetectedLabel: det ? det[1] : '',
    tiSampleN: rows.length === 1 ? '1 closed trade' : rows.length + ' closed trades',
    tiMedianHold: holdStr,
    tiPerDay: perDay.toFixed(1) + (perDay === 1 ? ' trade' : ' trades'),
    tiFacts: [
      { label: 'Hold time', value: cur[4], icon: 'ph-clock', fg: 'var(--text)' },
      { label: 'Trade freq', value: cur[5], icon: 'ph-chart-bar', fg: 'var(--text)' },
      {
        label: 'Max / day',
        value: maxN > 0 ? String(maxN) : 'Not set',
        icon: 'ph-list-checks',
        fg: maxN > 0 ? 'var(--text)' : 'var(--faint)',
      },
      {
        label: 'Target R:R',
        value: rrOk ? S.tiRR : S.tiRR || 'Not set',
        icon: 'ph-target',
        fg: rrOk ? 'var(--text)' : 'var(--faint)',
      },
      {
        label: 'Risk / trade',
        value: riskN > 0 ? riskN + '%' : 'Not set',
        icon: 'ph-shield',
        fg: riskN > 0 ? 'var(--text)' : 'var(--faint)',
      },
    ],
    tiStyles: D.styles.map((s) =>
      Object.assign(chip(S.tiStyle === s[0]), {
        label: s[1],
        subtitle: s[2],
        icon: s[3],
        holdTime: s[4],
        tradeFreq: s[5],
        iconFg: s[6],
        go: () => c.set({ tiStyle: s[0] }),
      }),
    ),
    tiExps: D.exp.map((e) =>
      Object.assign(chip(S.tiExp === e[0]), {
        label: e[1],
        desc: e[2],
        go: () => c.set({ tiExp: e[0] }),
      }),
    ),
    tiSessions: D.sessions.map((l) =>
      Object.assign(chip(S.tiSess.indexOf(l) >= 0), {
        label: l,
        go: () =>
          c.set((s) => ({
            tiSess: s.tiSess.indexOf(l) >= 0 ? s.tiSess.filter((x) => x !== l) : s.tiSess.concat(l),
          })),
      }),
    ),
    tiInstruments: D.instruments.map((l) =>
      Object.assign(chip(S.tiInst.indexOf(l) >= 0), {
        label: l,
        go: () =>
          c.set((s) => ({
            tiInst: s.tiInst.indexOf(l) >= 0 ? s.tiInst.filter((x) => x !== l) : s.tiInst.concat(l),
          })),
      }),
    ),
    tiSessCount: S.tiSess.length + ' selected',
    tiInstCount: S.tiInst.length + ' selected',
    tiCoverageNote: uncovered.length
      ? 'Your history includes ' +
        uncovered.join(', ') +
        ' — not in your selected instruments, so those trades are read against generic norms.'
      : 'Every instrument in your trade history is covered by this selection.',
    tiMax: S.tiMax,
    tiRR: S.tiRR,
    tiRisk: S.tiRisk,
    tiGoals: S.tiGoals,
    tiDesc: S.tiDescText,
    tiMaxHint: S.tiStyle === 'scalper' ? 'e.g. 40' : S.tiStyle === 'day_trader' ? 'e.g. 5' : 'e.g. 3',
    tiSetMax: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiMax: v, tiSaved: null });
    },
    tiSetRR: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiRR: v, tiSaved: null });
    },
    tiSetRisk: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiRisk: v, tiSaved: null });
    },
    tiSetDesc: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiDescText: v, tiSaved: null });
    },
    tiSetGoals: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiGoals: v, tiSaved: null });
    },
    tiGoalWords: goalWords === 0 ? 'Not written yet' : goalWords === 1 ? '1 word' : goalWords + ' words',
    tiRuleNote:
      (worst !== null
        ? 'Hitting your cap with every trade stopped out costs ' + worst.toFixed(1) + '% of equity.'
        : 'Set a daily cap and risk figure to see your worst-case day.') +
      (breakeven !== null ? ' At ' + S.tiRR + ', break-even win rate is ' + breakeven.toFixed(0) + '%.' : ''),
    tiEffects: [
      {
        icon: 'ph-list-checks',
        fg: 'var(--sky)',
        title: 'Trade count is read in context',
        body:
          maxN > 0
            ? 'A ' + maxN + '-trade day is normal for you — overtrading is only flagged past ' + maxN + '.'
            : 'Without a daily cap the coach falls back to a generic overtrading threshold.',
      },
      {
        icon: 'ph-globe-hemisphere-west',
        fg: 'var(--amb-2)',
        title: 'Sessions set the on-plan window',
        body: S.tiSess.length
          ? 'Entries outside ' + sessList + ' are marked off-plan in Journal, EOD and Analytics.'
          : 'No sessions selected — every entry counts as on-plan.',
      },
      {
        icon: 'ph-shield-check',
        fg: 'var(--pos)',
        title: 'R-multiples use your own risk unit',
        body:
          riskN > 0
            ? 'R is one ' + riskN + '% risk unit, so the R-distribution and expectancy match your sizing.'
            : 'Set risk per trade so R-multiples reflect your sizing rather than a default 1%.',
      },
    ],
    tiRules: (() => {
      const list = tiRuleList(c);
      return list.map((r, i) => {
        const t = c.tog(r.on);
        return Object.assign({ text: r.text, on: r.on, sys: r.sys, own: !r.sys }, t, {
          op: r.on ? '1' : '0.55',
          fg: r.on ? 'var(--text)' : 'var(--muted2)',
          deco: r.on ? 'none' : 'line-through',
          editing: S.tiRuleEditId === r.id,
          reading: S.tiRuleEditId !== r.id,
          toggleLabel: (r.on ? 'Disable rule: ' : 'Enable rule: ') + r.text,
          toggle: () =>
            tiSetRules(c, (a) => a.map((x) => (x.id === r.id ? Object.assign({}, x, { on: !x.on }) : x))),
          up: () => {
            if (i === 0) return;
            tiSetRules(c, (a) => {
              const b = a.slice();
              const p = b[i - 1],
                q = b[i];
              if (p !== undefined && q !== undefined) {
                b[i - 1] = q;
                b[i] = p;
              }
              return b;
            });
          },
          down: () => {
            if (i === list.length - 1) return;
            tiSetRules(c, (a) => {
              const b = a.slice();
              const n = b[i + 1],
                q = b[i];
              if (n !== undefined && q !== undefined) {
                b[i + 1] = q;
                b[i] = n;
              }
              return b;
            });
          },
          edit: () => c.set({ tiRuleEditId: r.id, tiRuleDraft: r.text }),
          remove: () => {
            tiSetRules(c, (a) => a.filter((x) => x.id !== r.id));
            c.flash('Rule removed', 'mut');
          },
        });
      });
    })(),
    tiRuleCount: (() => {
      const l = tiRuleList(c);
      return l.filter((r) => r.on).length + ' of ' + l.length + ' active';
    })(),
    tiRuleNew: S.tiRuleNew,
    tiRuleDraft: S.tiRuleDraft,
    tiSetRuleNew: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiRuleNew: v });
    },
    tiSetRuleDraft: (e: FieldEvent) => {
      const v = e.target.value;
      c.set({ tiRuleDraft: v });
    },
    tiRuleAdd: () => {
      const v = S.tiRuleNew.trim();
      if (!v) {
        c.flash('Type a rule first', 'neg');
        return;
      }
      tiSetRules(c, (a) => a.concat({ id: nextOwnId(a), text: v.slice(0, 500), on: true, sys: false }));
      c.set({ tiRuleNew: '' });
      c.flash('Rule added', 'pos');
    },
    tiRuleCommit: () => {
      const v = S.tiRuleDraft.trim();
      if (!v) {
        c.flash('A rule needs text', 'neg');
        return;
      }
      tiSetRules(c, (a) =>
        a.map((x) => (x.id === S.tiRuleEditId ? Object.assign({}, x, { text: v.slice(0, 500) }) : x)),
      );
      c.set({ tiRuleEditId: null, tiRuleDraft: '' });
    },
    tiRuleCancel: () => c.set({ tiRuleEditId: null, tiRuleDraft: '' }),
    tiRulesRestore: () => {
      const own = tiRuleList(c).filter((r) => !r.sys);
      c.set({
        tiRules: systemRules(c).concat(own),
        tiRuleEditId: null,
        tiSaved: null,
      });
      c.flash('Platform defaults restored', 'mut');
    },
    tiRuleFootnote: (() => {
      const l = tiRuleList(c),
        off = l.filter((r) => !r.on),
        own = l.filter((r) => !r.sys);
      const parts: string[] = [];
      parts.push(
        own.length
          ? own.length + (own.length === 1 ? ' rule is your own' : ' rules are your own')
          : 'All rules are platform defaults',
      );
      if (off.length)
        parts.push(
          off.length +
            (off.length === 1 ? ' is disabled and will not appear' : ' are disabled and will not appear') +
            ' in Rules Review',
        );
      parts.push(
        'Rules Followed in End-of-Day scores against the ' + l.filter((r) => r.on).length + ' active',
      );
      return parts.join(' · ') + '.';
    })(),
    tiSavedNote: S.tiSaved
      ? 'Saved ' + S.tiSaved + ' — applied across AI Insights, Journal and Analytics.'
      : 'Unsaved edits — the coach is still reading your last saved profile.',
    tiDetect: () => {
      c.set({ tiDetected: detectKey });
      c.flash('Detected ' + pick(detectKey)[1] + ' from ' + rows.length + ' trades', 'mut');
    },
    tiSave: () => {
      // Reference: `new Date()` → HH:MM. The port never reads the wall clock; the fixed reference present is used.
      c.set({ tiSaved: TI_SAVED_AT });
      c.flash('Trading identity saved — AI analysis now uses your norms', 'pos');
    },
  } satisfies Partial<ViewModel>;
}
export type StyleModel = ReturnType<typeof buildStyle>;
